import { PrivateRoute, useAuth } from '@garner-health/components-auth';
import { PageLayout } from '@garner-health/components-common';
import { useIdentifyForFeatureFlags } from '@garner-health/lib-ui-feature-flags';
import { logUser, rumUser } from '@garner-health/lib-ui-real-user-monitoring';
import React, { useEffect, useState } from 'react';
import {
  createBrowserRouter,
  Location,
  matchRoutes,
  Navigate,
  Outlet,
  RouteObject,
  useLocation,
} from 'react-router-dom';

import { Event } from '~/analytics';
import { analytics } from '~/analytics/segment';
import { getScopeInfo, getTokenClaims } from '~/auth';
import { bowserParser } from '~/browser';
import { AppFooter, AppHeader } from '~/components';
import { useAppState, useScopeInfo } from '~/contexts';
import logger from '~/logging';
import {
  AccountDetailsPage,
  ErrorPage,
  ForgotPasswordPage,
  LoginPage,
  NotFoundPage,
  ProviderDetailsPage,
  ResetPasswordConfirmationPage,
  ResetPasswordPage,
  SearchPage,
} from '~/pages';
import { SsoAuthPage } from '~/pages/sso-auth-page';

import { useAppBanners } from './use-app-banners';

const log = logger(__filename);

type RouteObjectWithData = RouteObject & {
  containScroll?: boolean;
};

const routeObjects: RouteObjectWithData[] = [
  {
    element: (
      <PrivateRoute>
        <SearchPage />
      </PrivateRoute>
    ),
    path: '/',
    containScroll: true,
  },
  {
    element: (
      <PrivateRoute>
        <SearchPage />
      </PrivateRoute>
    ),

    path: '/search',
    containScroll: true,
  },
  {
    path: '/account',
    element: (
      <PrivateRoute>
        <AccountDetailsPage />
      </PrivateRoute>
    ),
  },
  {
    path: '/providers/:id',
    element: (
      <PrivateRoute>
        <ProviderDetailsPage />
      </PrivateRoute>
    ),
  },
  {
    path: '/sign-in',
    element: <LoginPage />,
  },
  {
    path: '/login',
    element: <Navigate to="/sign-in" />,
  },
  {
    path: '/auth',
    element: <SsoAuthPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/send-reset-password-confirmation',
    element: <ResetPasswordConfirmationPage />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

function generatePathPatterns(routes: RouteObject[]): string[] {
  return routes
    .flatMap(route => {
      if (!route.children) return route.path || '/'; // NOSONAR
      return generatePathPatterns(route.children).map(p => `${route.path}/${p}`);
    })
    .filter(Boolean);
}

const pathPatterns = generatePathPatterns(routeObjects);

/** Util to get the pattern of the current path */
function getPathPattern(location: Location) {
  const matchedRoutes = matchRoutes(
    pathPatterns.map(path => ({ path })),
    location,
  );
  if (!matchedRoutes) return null;
  return matchedRoutes.find(m => m.pathname === location.pathname)?.route.path ?? null;
}

export const AppWrapper = () => {
  const location = useLocation();
  const auth = useAuth();
  const [containScroll, setContainScroll] = useState(false);
  const isDemo = !!useScopeInfo()?.isDemo;
  const { loadError } = useAppState();

  const identifyForFeatureFlags = useIdentifyForFeatureFlags();

  useEffect(() => {
    const path = getPathPattern(location);
    analytics.setGlobalProperties({ pageId: path });
    analytics.track(Event.NAVIGATE);

    const newContainScroll = routeObjects.find(m => m.path === path)?.containScroll ?? false;

    if (newContainScroll !== containScroll) {
      setContainScroll(newContainScroll);
    }
  }, [location]);

  useEffect(() => {
    if (auth.state === 'LOGGED_IN') {
      let ignore = false;
      analytics.authSegment().catch(err => log.error({ err }, 'Error initializing segment'));
      Promise.all([getTokenClaims(), getScopeInfo()])
        .then(([{ clientId, careNavigatorId }, { plan, isDemo: isDemoScope }]) => {
          if (ignore) return;
          // Internal identifiers only — no name/email — so RUM sessions and logs are attributable
          // without carrying PII/PHI. Guard against missing claims (Number(undefined) === NaN) so
          // sessions aren't all collapsed under a bogus "NaN" identity.
          if (Number.isFinite(careNavigatorId) && Number.isFinite(clientId)) {
            const datadogUser = {
              id: careNavigatorId.toString(),
              clientId: clientId.toString(),
              plan,
              isDemo: isDemoScope,
            };
            rumUser.setUser(datadogUser);
            logUser.setUser(datadogUser);
          } else {
            rumUser.clearUser();
            logUser.clearUser();
          }

          identifyForFeatureFlags({
            kind: 'datapro',
            key: careNavigatorId.toString(),
            clientId: clientId.toString(),
            appVersion: GARNER_VERSION,
            browser: bowserParser.getBrowserName(),
          });
        })
        .catch(err => {
          if (ignore) return;
          // Clear any previously-set user so a claims-load failure (e.g. token refresh error or
          // account switch) doesn't leave later telemetry attributed to the prior user.
          rumUser.clearUser();
          logUser.clearUser();
          log.error({ err }, 'Error getting token claims to identify user for feature flags');
        });
      return () => {
        ignore = true;
      };
    } else {
      rumUser.clearUser();
      logUser.clearUser();
    }
  }, [auth, auth.state, identifyForFeatureFlags, log]);

  const banners = useAppBanners(isDemo);

  if (loadError) throw loadError;

  return (
    <PageLayout containScroll={containScroll}>
      <PageLayout.Header divider banners={banners}>
        <AppHeader />
      </PageLayout.Header>
      <PageLayout.Main paddingTop="xs" paddingBottom="none" paddingX="none">
        <Outlet />
      </PageLayout.Main>
      <PageLayout.Footer divider paddingTop="xs" paddingBottom="xs">
        <AppFooter />
      </PageLayout.Footer>
    </PageLayout>
  );
};

export const appRoutes: RouteObject[] = [
  {
    element: <AppWrapper />,
    errorElement: <ErrorPage />,
    children: routeObjects,
  },
];

export const router = createBrowserRouter(appRoutes);
